import React, { useContext, useRef } from 'react';
import { Carousel } from '@one-thd/sui-carousel';
import { Typography, Button } from '@one-thd/sui-atomic-components';
import {
  useStoreId,
  useStore,
} from '@thd-nucleus/experience-context';
import {
  QueryProvider,
  QueryContext,
} from '@thd-nucleus/data-sources';
import { ImpressionProvider, useImpression } from '@thd-olt-component-react/impression';
import { NoProductsMessage } from './NoProductsMessage';
import { CategoryCarouselPropType, CategoryCarouselDefaultPropType } from './Helper';
import { CustomProductPod } from './CustomProductPod';

const CategoryCarousel = ({
  endCapData,
  exclude,
  categoryName,
  categoryImageURL,
  categoryURL,
  categoryShopAllLink,
  channel,
  displayImageOnMobileWeb,
  title,
  componentName,
  entryId
}) => {
  const carouselRef = useRef(null);
  const storeId = useStoreId();
  const { storeZip: zipCode } = useStore();
  const { isClientResolved } = useContext(QueryContext);
  const localStoreId = useRef(storeId);
  const hasProducts = (endCapData?.products || []).filter((product) => {
    return (exclude || []).indexOf(product.itemId) === -1;
  }).length !== 0;

  const handleClickedProduct = (productIndex) => {
    if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS) {
      window.LIFE_CYCLE_EVENT_BUS.trigger(
        `${componentName}.click`,
        productIndex
      );
    }
  };
  const properUrl = categoryShopAllLink !== '' ? categoryShopAllLink : categoryURL;
  const showCategoryBlock = categoryImageURL && properUrl;
  const dataSources = endCapData?.products?.[0]?.dataSources === 'personalizedDeals'
    ? 'personalizedDeals'
    : 'topDeals';
  const cacheKey = dataSources && dataSources === 'personalizedDeals'
    ? 'personalized-deals-child'
    : 'top-deals-child';

  let impressionName = title.replace(/\s/g, '');
  let impressionCategoryName = categoryName.replace(/\W+/g, '');

  const impressionData = {
    id: entryId || '',
    component: 'Endcap',
    name: impressionName || '',
    type: 'product',
    position: '',
    category: impressionCategoryName || ''
  };

  return (
    <ImpressionProvider data={impressionData}>
      <div>
        {!hasProducts && (
          <NoProductsMessage
            categoryName={categoryName !== 'All' ? categoryName : ''}
            imageUrl={categoryImageURL}
            url={properUrl}
            title={title}
          />
        )}
        {hasProducts && (
          <div className="endcap-section sui-w-full sui-flex sui-gap-4 sui-pt-5">
            {showCategoryBlock && (
              <div className="category-block
              sui-flex sui-items-center sui-justify-center
              sui-border-1 sui-border-primary sui-border-solid
              sui-w-auto sui-p-1 sm:sui-px-3"
              >
                <div className="sui-flex sui-flex-col sui-items-center sui-gap-4 sui-w-40 sm:sui-w-48">
                  <Typography variant="h4" align="center">{categoryName !== 'All' ? categoryName : ''}</Typography>
                  <div className="sui-flex sui-justify-center sui-w-28">
                    <img
                      className="sui-w-auto sui-max-w-full sui-animate-pulse"
                      src={categoryImageURL}
                      width="1"
                      height="1"
                      alt=""
                    />
                  </div>
                  <Button
                    href={properUrl}
                    variant="text"
                  >Shop All
                  </Button>
                </div>
              </div>
            )}
            <div
              id={'digital-endcap-' + endCapData.catName}
              ref={carouselRef}
              className="digital-endcap sui-w-full sui-overflow-hidden"
            >
              <QueryProvider
                dataSource={endCapData?.products?.[0]?.dataSources}
                dataSources={endCapData?.products?.[0]?.dataSources}
                defaultVariables={{
                  storeId: localStoreId.current,
                  zipCode,
                  skipSpecificationGroup: true
                }}
                cacheKey={cacheKey}
              >
                <Carousel
                  slidesPerView={showCategoryBlock ? 1 : 2}
                  breakpoints={{
                    sm: {
                      slidesPerView: showCategoryBlock ? 2 : 3,
                      slidesPerGroup: showCategoryBlock ? 2 : 3,
                    },
                    md: {
                      slidesPerView: showCategoryBlock ? 2 : 4,
                      slidesPerGroup: showCategoryBlock ? 2 : 4,
                    },
                    lg: {
                      slidesPerView: showCategoryBlock ? 3 : 4,
                      slidesPerGroup: showCategoryBlock ? 3 : 4,
                    },
                    xl: {
                      slidesPerView: showCategoryBlock ? 5 : 5,
                      slidesPerGroup: showCategoryBlock ? 5 : 5,
                    }
                  }}
                  initialSlide={0}
                  disableMargin
                >
                  {(endCapData?.products || [])
                    .filter((product) => {
                      return (exclude || []).indexOf(product.itemId) === -1;
                    })
                    .map((product, productIndex) => (
                      <div key={productIndex}>
                        <div
                          id={'top-deals__wrapper-' + productIndex}
                          className="endcap-pod-wrapper"
                        >
                          <CustomProductPod itemId={product.itemId} storeId={storeId} productIndex={productIndex} />
                        </div>
                      </div>
                    ))}
                </Carousel>
              </QueryProvider>
            </div>
          </div>
        )}
      </div>
    </ImpressionProvider>
  );
};

CategoryCarousel.displayName = 'CategoryCarousel';
CategoryCarousel.propTypes = CategoryCarouselPropType;
CategoryCarousel.defaultProps = CategoryCarouselDefaultPropType;

export { CategoryCarousel };
